import React, { useState, useEffect } from "react";
import axios from "axios";
import "./UploadModal.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const UploadContextlyModal = ({ isOpen, onClose, onSubmit, existingSources }) => {
  const [activeTab, setActiveTab] = useState("pdf");
  const [newFiles, setNewFiles] = useState([]);
  const [newLinks, setNewLinks] = useState([]);
  const [newTexts, setNewTexts] = useState([]);
  const [tempLink, setTempLink] = useState("");
  const [tempText, setTempText] = useState("");
  const [fileError, setFileError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [textError, setTextError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filesToDisplay = [...(existingSources?.files || []), ...newFiles];
  const linksToDisplay = [...(existingSources?.links || []), ...newLinks];
  const textsToDisplay = [...(existingSources?.texts || []), ...newTexts];

  useEffect(() => {
    if (!isOpen) {
      setNewFiles([]); setNewLinks([]); setNewTexts([]);
      setTempLink(""); setTempText("");
      setFileError(""); setLinkError(""); setTextError("");
      setActiveTab("pdf"); setIsSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleFileChange = (e) => {
    if (isSubmitting) return;
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      if (filesToDisplay.length >= 10) { setFileError("You can upload a maximum of 10 files."); return; }
      if (newFiles.some((file) => file.name === selectedFile.name) || (existingSources?.files || []).some((file) => file.name === selectedFile.name)) {
        setFileError("This file is already added."); setTimeout(() => setFileError(""), 5000); return;
      }
      setNewFiles([...newFiles, selectedFile]); setFileError("");
    } else { setFileError("Please upload a valid PDF file."); setTimeout(() => setFileError(""), 5000); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile.type === "application/pdf") {
      if (filesToDisplay.length >= 10) { setFileError("You can upload a maximum of 10 files."); setTimeout(() => setFileError(""), 5000); return; }
      if (newFiles.some((file) => file.name === droppedFile.name) || (existingSources?.files || []).some((file) => file.name === droppedFile.name)) {
        setFileError("This file is already added."); setTimeout(() => setFileError(""), 5000); return;
      }
      setNewFiles([...newFiles, droppedFile]); setFileError("");
    } else { setFileError("Please upload a valid PDF file."); setTimeout(() => setLinkError(""), 5000); }
  };

  const handleSaveLink = () => {
    if (isSubmitting) return;
    if (linksToDisplay.includes(tempLink.trim())) { setLinkError("This link is already added."); setTimeout(() => setLinkError(""), 5000); return; }
    if (tempLink.trim() !== "" && linksToDisplay.length < 5) { setNewLinks([...newLinks, tempLink.trim()]); setTempLink(""); setLinkError(""); }
  };

  const handleSaveText = () => {
    if (isSubmitting) return;
    if (tempText.trim() === "") { setTextError("Text cannot be empty."); setTimeout(() => setTextError(""), 5000); return; }
    setNewTexts([...newTexts, tempText.trim()]); setTempText(""); setTextError("");
  };

  const handleDeleteFile = (file) => setNewFiles(newFiles.filter((f) => f !== file));
  const handleDeleteLink = (link) => setNewLinks(newLinks.filter((l) => l !== link));
  const handleDeleteText = (text) => setNewTexts(newTexts.filter((t) => t !== text));

  const handleSubmit = async () => {
    if (!newFiles.length && !newLinks.length && !newTexts.length) {
      setFileError("Please provide at least one new input."); setTimeout(() => setFileError(""), 5000); return;
    }
    setFileError(""); setLinkError(""); setTextError(""); setErrorMessage("");
    setIsLoading(true); setIsSubmitting(true);
    try {
      const sessionId = sessionStorage.getItem("currentSessionId");
      if (!sessionId) throw new Error("No active session found. Please start a new session.");
      const formData = new FormData();
      newFiles.forEach((file) => formData.append("files", file));
      const urlParams = new URLSearchParams();
      newLinks.forEach((link) => urlParams.append("urls", link));
      newTexts.forEach((text) => {
        const textBlob = new Blob([text], { type: "text/plain" });
        const textFile = new File([textBlob], `${text.substring(0, 10)}.txt`);
        formData.append("files", textFile);
      });
      const resourceResponse = await axios.post(
        `${BASE_URL}/sessions/${sessionId}/resources?${urlParams.toString()}`,
        formData, { headers: { "Content-Type": "multipart/form-data" } }
      );
      const enrichedResources = [];
      for (const resource of resourceResponse.data.resources) {
        try {
          const guideResponse = await axios.get(`${BASE_URL}/sessions/${sessionId}/resources/${resource.resource_id}/source_guide`);
          const enrichedResource = { ...resource, summary: guideResponse.data.summary, key_topics: guideResponse.data.key_topics };
          sessionStorage.setItem(`source_Guide_${resource.resource_id}`, JSON.stringify(enrichedResource));
          enrichedResources.push(enrichedResource);
        } catch (error) {
          if (error.response && error.response.status === 429) {
            console.error(`Rate limit exceeded for resource ${resource.resource_id}:`, error);
            setErrorMessage(error.response.data.detail || "Google Gemini API rate limit exceeded. Please try again later.");
          } else {
            console.error(`Error retrieving source guide for resource ${resource.resource_id}:`, error); 
          }
        }
      }
      onSubmit({
        files: enrichedResources.filter((res) => res.filename),
        links: enrichedResources.filter((res) => res.url),
        texts: enrichedResources.filter((res) => res.filename && res.filename.endsWith(".txt")),
      });
      onClose();
    } catch (error) {
      console.error("Error during upload process:", error);
      if (error.response && error.response.status === 429) {
        setErrorMessage(error.response.data.detail || "Google Gemini API rate limit exceeded. Please try again later.");
      } else {
        setErrorMessage("An error occurred while processing. Please try again.");
      }
    } finally { setIsLoading(false); setIsSubmitting(false); }
  };

  const validateUrl = (url) => /^(https?:\/\/(?:www\.)?[^\s/.?#]+\.[^\s]*)$/.test(url);

  if (!isOpen) return null;

  return (
    <div className="import-overlay" onClick={onClose}>
      <div className="import-modal" onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <div className="import-loading">
            <div className="import-spinner"></div>
            <p>We are getting things ready for you...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="import-header">
              <div className="import-header-left">
                <i className="fa fa-magic import-header-icon"></i>
                <div>
                  <h2 className="import-title">Import Sources</h2>
                  <p className="import-subtitle">Add context data to train your Contextly AI model.</p>
                </div>
              </div>
              <button className="import-close" onClick={onClose} disabled={isSubmitting}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="import-tabs">
              <button className={`import-tab ${activeTab === 'pdf' ? 'active' : ''}`} onClick={() => setActiveTab('pdf')}>
                <i className="fa fa-file-pdf"></i> PDF Upload
              </button>
              <button className={`import-tab ${activeTab === 'url' ? 'active' : ''}`} onClick={() => setActiveTab('url')}>
                <i className="fa fa-link"></i> URL Import
              </button>
              <button className={`import-tab ${activeTab === 'text' ? 'active' : ''}`} onClick={() => setActiveTab('text')}>
                <i className="fa fa-font"></i> Text Blocks
              </button>
            </div>

            {/* Tab Content */}
            <div className="import-body">
              {/* PDF Upload Tab */}
              {activeTab === 'pdf' && (
                <div className="import-tab-content">
                  <div className="import-dropzone" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
                    <div className="dropzone-icon"><i className="fa fa-cloud-upload-alt"></i></div>
                    <p className="dropzone-text"><strong>Click to upload or drag and drop</strong></p>
                    <p className="dropzone-hint">Support for PDF, DOCX, and TXT (Max 10MB per file)</p>
                    <input type="file" onChange={handleFileChange} accept="application/pdf" hidden id="file-input" disabled={isSubmitting} />
                    <label htmlFor="file-input" className="dropzone-label"></label>
                  </div>
                  {fileError && <p className="import-error">{fileError}</p>}

                  {filesToDisplay.filter((f) => typeof f === "string" ? f.endsWith(".pdf") : f?.name?.endsWith(".pdf")).length > 0 && (
                    <div className="import-file-list">
                      <h4 className="file-list-title">UPLOADED FILES ({filesToDisplay.filter((f) => typeof f === "string" ? f.endsWith(".pdf") : f?.name?.endsWith(".pdf")).length}/10)</h4>
                      {filesToDisplay.filter((f) => typeof f === "string" ? f.endsWith(".pdf") : f?.name?.endsWith(".pdf")).map((file, index) => (
                        <div key={index} className="file-list-item">
                          <div className="file-icon-badge pdf"><i className="fa fa-file-pdf"></i></div>
                          <div className="file-info">
                            <span className="file-name">{typeof file === "string" ? file : file.name}</span>
                            <span className="file-meta">{typeof file === "object" ? (file.size / (1024 * 1024)).toFixed(1) + ' MB' : ''} · Complete</span>
                          </div>
                          {newFiles.includes(file) && (
                            <button className="file-delete" onClick={() => handleDeleteFile(file)}><i className="fa fa-times"></i></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* URL Import Tab */}
              {activeTab === 'url' && (
                <div className="import-tab-content">
                  <h4 className="tab-section-title">ADD WEB SOURCE</h4>
                  <div className="url-input-row">
                    <div className="url-input-wrapper">
                      <i className="fa fa-link url-input-icon"></i>
                      <input
                        type="text"
                        className="url-input"
                        placeholder="https://example.com/article"
                        value={tempLink}
                        onChange={(e) => { setTempLink(e.target.value); setLinkError(validateUrl(e.target.value) ? "" : "Please enter a valid URL."); }}
                        disabled={isSubmitting}
                      />
                    </div>
                    <button className="url-add-btn" disabled={!validateUrl(tempLink) || linksToDisplay.length >= 5 || isSubmitting} onClick={handleSaveLink}>
                      <i className="fa fa-plus"></i> Add
                    </button>
                  </div>
                  {linkError && <p className="import-error">{linkError}</p>}

                  {linksToDisplay.length > 0 && (
                    <div className="import-file-list">
                      {linksToDisplay.map((link, index) => (
                        <div key={index} className="file-list-item">
                          <div className="file-icon-badge web"><i className="fa fa-globe"></i></div>
                          <div className="file-info">
                            <span className="file-name">{link}</span>
                            <span className="file-meta file-synced"><i className="fa fa-check-circle"></i> Scraped successfully</span>
                          </div>
                          {newLinks.includes(link) && (
                            <button className="file-delete" onClick={() => handleDeleteLink(link)}><i className="fa fa-times"></i></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Text Blocks Tab */}
              {activeTab === 'text' && (
                <div className="import-tab-content">
                  <div className="text-header">
                    <h4 className="tab-section-title">RAW TEXT INPUT</h4>
                    <span className="text-counter">{tempText.length} / 1000</span>
                  </div>
                  <textarea
                    className="text-input"
                    placeholder="Paste context or type directly here..."
                    value={tempText}
                    onChange={(e) => setTempText(e.target.value.slice(0, 1000))}
                    disabled={isSubmitting}
                    maxLength={1000}
                  />
                  <button className="text-save-btn" disabled={tempText.trim() === "" || isSubmitting} onClick={handleSaveText}>
                    Save Block
                  </button>
                  {textError && <p className="import-error">{textError}</p>}

                  {textsToDisplay.length > 0 && (
                    <div className="import-file-list">
                      {textsToDisplay.map((text, index) => (
                        <div key={index} className="file-list-item">
                          <div className="file-icon-badge text"><i className="fa fa-file-alt"></i></div>
                          <div className="file-info">
                            <span className="file-name">Text Source {index + 1}</span>
                            <span className="file-meta">{text.length} chars</span>
                          </div>
                          {newTexts.includes(text) && (
                            <button className="file-delete" onClick={() => handleDeleteText(text)}><i className="fa fa-trash"></i></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {errorMessage && <p className="import-error" style={{ padding: '0 24px' }}>{errorMessage}</p>}

            {/* Footer */}
            <div className="import-footer">
              <div className="import-secure">
                <i className="fa fa-check-circle"></i>
                <span>Contextly AI Secure Upload</span>
              </div>
              <div className="import-footer-actions">
                <button className="import-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                <button className="import-proceed" onClick={handleSubmit} disabled={isSubmitting}>
                  Proceed <i className="fa fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadContextlyModal;
